import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { areaService } from '../services/area.service';
import { Area } from '../../interfaces/areas';
import { Envelope } from '../../interfaces/envelope';
import { Room } from '../../interfaces/rooms';

interface AreaState {
	areas: {
		[id: string]: Area;
	};
	areaRooms: {
		[id: string]: Room[];
	};
	selectedAreas: { [key: string]: Area };
	isLoading: boolean;
}

const initialState = {
	areas: {},
	areaRooms: {},
	selectedAreas: {},
	isLoading: false,
} as AreaState;

export const fetchAreas = createAsyncThunk<
	Area[],
	void,
	{
		rejectValue: Envelope<[]> | unknown;
	}
>('area/fetchAreas', async (_undefined, { rejectWithValue }) => {
	try {
		return await areaService.fetchAreas();
	} catch (error) {
		return rejectWithValue(error);
	}
});

const areaSlice = createSlice({
	name: 'area',
	initialState,
	reducers: {
		setAreaRooms: (state, action: PayloadAction<Room[]>) => {
			const ret: { [id: number]: Room[] } = {};
			state.areaRooms = Object.values(state.areas).reduce((obj, area) => {
				obj[area.id] = action.payload.filter((room) => room.fk_area === area.id);
				return obj;
			}, ret);
		},
		updateAreaRoom: (state, action: PayloadAction<Room>) => {
			state.areaRooms[action.payload.fk_area][
				state.areaRooms[action.payload.fk_area].findIndex((el) => el.id === action.payload.id)
			] = action.payload;
		},
		selectArea: (state, action: PayloadAction<string>) => {
			state.selectedAreas = {
				...state.selectedAreas,
				[action.payload]: state.areas[action.payload],
			};
		},
		deSelectArea: (state, action: PayloadAction<string>) => {
			delete state.selectedAreas[action.payload];
		},
		resetArea: (state) => {
			Object.assign(state, initialState);
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchAreas.fulfilled, (state, action) => {
			const ret: { [id: number]: Area } = {};
			const objectAreas = action.payload.reduce((obj, area) => {
				obj[area.id] = area;
				return obj;
			}, ret);

			state.areas = objectAreas;
			state.selectedAreas = objectAreas;
			state.isLoading = false;
		});
		builder.addCase(fetchAreas.pending, (state) => {
			state.isLoading = true;
		});
		builder.addCase(fetchAreas.rejected, (state, action) => {
			state.areas = {};
			state.selectedAreas = {};
			state.isLoading = false;
		});
	},
});

export const { setAreaRooms, updateAreaRoom, selectArea, deSelectArea, resetArea } =
	areaSlice.actions;

export default areaSlice;
